import Skeleton from '@carvana/ds-skeleton';
import cx from 'classnames';

type OwnProps = {
  isLite?: boolean;
};

export default function VehicleSkeletonLayout({ isLite = false }: OwnProps) {
  return (
    <main
      aria-label="Loading vehicle details"
      aria-live="polite"
      className="bg-background-default"
      role="status"
    >
      <div className="mx-auto max-w-[1440px] px-0 md:px-3xl lg:px-[84px]">
        {/* Breadcrumbs */}
        <div className="flex items-center justify-between px-2xl py-lg md:px-0">
          <div className="flex items-center gap-sm">
            <Skeleton height="14px" width="52px" />
            <Skeleton height="14px" width="8px" />
            <Skeleton height="14px" width="36px" />
            <Skeleton height="14px" width="8px" />
            <Skeleton height="14px" width="120px" />
          </div>
          <div className="flex gap-lg">
            <Skeleton height="24px" width="24px" />
            <Skeleton height="24px" width="24px" />
          </div>
        </div>

        {/* Grid — matches real VDP: grid-cols-1 → sm:grid-cols-2 → md:grid-cols-[2fr_1fr] */}
        <div className="relative grid grid-cols-1 gap-x-2xl bg-background-default sm:grid-cols-2 md:grid-cols-[minmax(0,2fr)_1fr] lg:gap-x-4xl">
          {/* Row 1, Col 1: Hero image + thumbnail strip */}
          <div>
            <Skeleton className="!h-[300px] !w-full sm:!h-[400px] md:!h-[460px]" />
            <div className="flex gap-sm px-2xl py-sm md:px-0">
              <Skeleton className="!h-[80px] flex-1" />
              <Skeleton className="!h-[80px] flex-1" />
              <Skeleton className="!h-[80px] flex-1" />
              <Skeleton className="!h-[80px] flex-1" />
            </div>
          </div>

          {/* Col 2, rows 1–3: Sidebar
              Standard (isLite=false): sm:row-start-3 md:row-start-1
                → at tablet sidebar drops below hero; at desktop it sits beside it.
              Lite (isLite=true): sm:row-start-1
                → sidebar beside hero from tablet up. */}
          <div
            className={cx(
              'z-10 sm:col-start-2 sm:row-span-3 sm:pb-5xl sm:pr-2xl md:pb-7xl md:pr-0',
              isLite ? 'sm:row-start-1' : 'sm:row-start-3 md:row-start-1'
            )}
          >
            <div className="sticky top-[var(--sticky-info-card-top-sm,32px)] flex flex-col gap-3xl px-2xl py-3xl sm:px-0 md:top-[var(--sticky-info-card-top-md,32px)]">
              {/* Badge */}
              <Skeleton className="!rounded-full" height="24px" width="88px" />
              {/* Vehicle title */}
              <div className="flex flex-col gap-sm">
                <Skeleton className="!w-full" height="34px" />
                <Skeleton height="34px" width="75%" />
                <Skeleton height="18px" width="56%" />
              </div>
              {/* Price block */}
              <div className="flex flex-col gap-sm">
                <div className="flex items-baseline gap-md">
                  <Skeleton height="32px" width="130px" />
                  <Skeleton height="18px" width="100px" />
                </div>
                {/* Incentives row */}
                <Skeleton className="!w-full !rounded-lg" height="40px" />
                <Skeleton height="18px" width="180px" />
                <Skeleton height="16px" width="120px" />
                <Skeleton height="18px" width="200px" />
              </div>
              {/* CTA */}
              <Skeleton className="!h-[52px] !w-full !rounded-full" />
            </div>
          </div>

          {/* Row 2, Col 1: DesktopPageNavigation (nav tabs) — hidden on mobile */}
          <div className="hidden items-center gap-4xl border-b border-border-weak px-2xl py-lg sm:flex md:px-0">
            <Skeleton height="20px" width="68px" />
            <Skeleton height="20px" width="60px" />
            <Skeleton height="20px" width="96px" />
            <Skeleton height="20px" width="52px" />
            <Skeleton height="20px" width="76px" />
          </div>

          {/* Row 3, Col 1: MainContentWrapper */}
          <div className="row-start-3 flex flex-col gap-4xl px-2xl py-4xl sm:pl-2xl md:pl-0">
            <div className="flex flex-col gap-md">
              <Skeleton height="20px" width="140px" />
              <Skeleton className="!h-[140px] !w-full !rounded-lg" />
            </div>
            {!isLite && (
              <div className="flex flex-col gap-md">
                <Skeleton height="20px" width="160px" />
                <Skeleton className="!h-[100px] !w-full" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile sticky CTA */}
      <div className="fixed inset-x-0 bottom-0 flex gap-md border-t border-border-weak bg-background-default px-2xl py-lg sm:hidden">
        <Skeleton className="!h-[52px] flex-1 !rounded-full" />
      </div>
    </main>
  );
}
